import React from 'react';
import { Typography, TypographyProps, SxProps, Theme } from '@mui/material';
import { colors, typographyVariants } from '../../tokens';

export type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface HeadingProps extends Omit<TypographyProps, 'variant' | 'component'> {
  /**
   * The heading level (h1-h6)
   * @default 'h2'
   */
  level?: HeadingLevel;
  /**
   * The content to display
   */
  children: React.ReactNode;
  /**
   * Custom styles to apply
   */
  sx?: SxProps<Theme>;
  /**
   * Color variant
   * @default 'primary'
   */
  colorVariant?: 'primary' | 'secondary' | 'muted';
}

/**
 * Heading - Semantic heading component for page and section titles
 * 
 * Use cases:
 * - Page titles (h1, h2)
 * - Section headings (h3, h4)
 * - Subsection headings (h5, h6)
 * - Feature titles
 * - Card headers
 * 
 * @example
 * ```tsx
 * <Heading level="h1">My Songs</Heading>
 * <Heading level="h3">Recent Activity</Heading>
 * <Heading level="h5" colorVariant="muted">Settings</Heading>
 * ```
 */
export const Heading: React.FC<HeadingProps> = ({ 
  level = 'h2',
  children, 
  sx = {}, 
  colorVariant = 'primary',
  ...props 
}) => {
  const colorMap = {
    primary: colors.text.primary,
    secondary: colors.text.secondary,
    muted: colors.text.tertiary,
  };

  const fontSizeMap = {
    h1: { xs: '1.75rem', md: '2.5rem' },
    h2: { xs: '1.5rem', md: '2rem' },
    h3: { xs: '1.25rem', md: '1.75rem' },
    h4: { xs: '1.125rem', md: '1.5rem' },
    h5: { xs: '1rem', md: '1.25rem' },
    h6: { xs: '0.875rem', md: '1rem' },
  };

  const fontWeightMap = {
    h1: 700,
    h2: 700,
    h3: 600,
    h4: 600,
    h5: 600,
    h6: 600,
  };

  return (
    <Typography
      component={level}
      sx={{
        fontFamily: typographyVariants.pageHeading.fontFamily,
        fontWeight: fontWeightMap[level],
        lineHeight: typographyVariants.pageHeading.lineHeight,
        color: colorMap[colorVariant],
        fontSize: fontSizeMap[level],
        ...sx,
      }}
      {...props}
    >
      {children}
    </Typography>
  );
};
